// Apple Sign In via OAuth popup for web/Telegram.
// Uses response_mode=form_post — Apple POSTs to server callback which stores
// the token by state. Client polls GET /auth/apple/status?state=xxx until ready.
import { getApiBaseUrl } from '../api/http_client.js';

const APPLE_SERVICES_ID  = 'com.matchstars.royalsmash.web';
const CACHED_KEY         = 'native_apple_id';
const POLL_INTERVAL_MS   = 1000;
const TIMEOUT_MS         = 5 * 60 * 1000;

function getBaseUrl(): string
{
  return getApiBaseUrl().replace( /\/+$/, '' );
}

async function pollForToken( state: string ): Promise<{ identityToken: string; userId: string } | null>
{
  try
  {
    const res = await fetch( `${ getBaseUrl() }/api/auth/apple/status?state=${ state }` );
    if ( !res.ok ) return null;
    const data = await res.json() as { pending?: boolean; identityToken?: string; userId?: string };
    if ( data.pending || !data.identityToken ) return null;
    return { identityToken: data.identityToken, userId: data.userId ?? '' };
  }
  catch
  {
    return null;
  }
}

function openApplePopup(): Promise<{ identityToken: string; userId: string }>
{
  return new Promise( ( resolve, reject ) =>
  {
    const state = `web_${ crypto.randomUUID() }`;

    const params = new URLSearchParams( {
      client_id:     APPLE_SERVICES_ID,
      redirect_uri:  `${ getBaseUrl() }/api/auth/apple/callback`,
      response_type: 'code id_token',
      response_mode: 'form_post',
      scope:         'openid email name',
      nonce:         crypto.randomUUID(),
      state,
    } );

    console.log( '[AppleWebSignIn] redirect_uri:', `${ getBaseUrl() }/api/auth/apple/callback` );

    const popup = window.open(
      `https://appleid.apple.com/auth/authorize?${ params }`,
      'apple-signin',
      'width=500,height=600,left=200,top=100'
    );

    if ( !popup )
    {
      reject( new Error( 'Popup blocked' ) );
      return;
    }

    let done = false;

    const clearAll = () =>
    {
      clearInterval( pollTimer );
      clearInterval( closeTimer );
      clearTimeout( timeoutTimer );
    };

    const pollTimer = setInterval( async () =>
    {
      if ( done ) return;
      const result = await pollForToken( state );
      if ( !result ) return;
      done = true;
      clearAll();
      popup.close();
      console.log( '[AppleWebSignIn] token received via polling' );
      resolve( result );
    }, POLL_INTERVAL_MS );

    const closeTimer = setInterval( async () =>
    {
      if ( done ) return;
      if ( popup.closed )
      {
        clearAll();
        const result = await pollForToken( state );
        if ( result )
        {
          done = true;
          resolve( result );
        }
        else
        {
          done = true;
          reject( new Error( 'Closed by user' ) );
        }
      }
    }, 500 );

    const timeoutTimer = setTimeout( () =>
    {
      if ( done ) return;
      done = true;
      clearAll();
      popup.close();
      reject( new Error( 'Apple Sign-In timeout' ) );
    }, TIMEOUT_MS );
  } );
}

export const AppleWebSignIn = {
  isAvailable(): boolean
  {
    return true;
  },

  getCachedId(): Promise<string | null>
  {
    return Promise.resolve( localStorage.getItem( CACHED_KEY ) );
  },

  setCachedId( id: string ): void
  {
    localStorage.setItem( CACHED_KEY, id );
  },

  async signIn(): Promise<{ identityToken: string; userId: string }>
  {
    return openApplePopup();
  },

  signOut(): Promise<void>
  {
    localStorage.removeItem( CACHED_KEY );
    return Promise.resolve();
  },
};
